import { jsxs, jsx } from "react/jsx-runtime";
import { Stack, Typography } from "@mui/material";
import { memo, useId, useCallback } from "react";
import { LabelOptional } from "../common/LabelOptional.js";
import { Textarea } from "./Textarea.js";
import { textareaControlStyles } from "./styles.js";
import { Nowrap } from "../Nowrap.js";
import { InputTip } from "../common/InputTip/InputTip.js";
import { Label } from "../common/Label.js";
import { spreadSx } from "../../utils/spreadSx.js";
import { inheritDataTestId } from "../../utils/dataTestId.js";
import { DATA_TEST_ID_INHERITED } from "../../constants/DATA_TEST_ID_INHERITED.js";
const TextareaControl = memo(
  ({
    optional,
    label,
    error,
    errorMaxSymbols,
    className,
    tip,
    max,
    onChange,
    sx,
    ignoreLabelHeight,
    ignoreHintHeight = !0,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    readOnly,
    size = "md",
    disabled,
    fixedRowsCount = !1,
    ...restProps
  }) => {
    var _a, _b;
    const id = useId(), handleChange = useCallback(
      (e) => onChange == null ? void 0 : onChange(e),
      [onChange]
    ), symbolsLeft = max ? max - (((_a = restProps.value) == null ? void 0 : _a.length) ?? 0) : 0, symbolsEntered = ((_b = restProps.value) == null ? void 0 : _b.length) ?? 0, limitReachedError = max !== void 0 && symbolsLeft < 0 ? errorMaxSymbols ?? "Character limit exceeded" : void 0, styles = textareaControlStyles, isError = !!(error || limitReachedError), hint = /* @__PURE__ */ jsx(
      InputTip,
      {
        size: "md",
        hide: !1,
        disabled,
        color: isError ? "error" : "default",
        ignoreHeight: ignoreHintHeight,
        children: error || limitReachedError || tip
      }
    );
    return /* @__PURE__ */ jsxs(
      Stack,
      {
        sx: [
          { ".MuiTextField-root": { flex: 1 }, width: "100%" },
          ...spreadSx(sx)
        ],
        className,
        "data-test-id": inheritDataTestId(restProps, "container"),
        children: [
          label || optional ? /* @__PURE__ */ jsxs(
            Stack,
            {
              sx: [
                ignoreLabelHeight ? { position: "absolute", top: "100%", left: 0 } : {},
                styles.labelRow
              ],
              children: [
                label && /* @__PURE__ */ jsx(
                  Label,
                  {
                    htmlFor: id,
                    size: "md",
                    disabled,
                    "data-test-id": inheritDataTestId(restProps, "label"),
                    children: /* @__PURE__ */ jsx(Nowrap, { children: label })
                  }
                ),
                /* @__PURE__ */ jsx(LabelOptional, { optional, disabled: !!disabled })
              ]
            }
          ) : null,
          /* @__PURE__ */ jsx(
            Textarea,
            {
              "data-test-id": DATA_TEST_ID_INHERITED,
              readOnly,
              id,
              disabled,
              ...restProps,
              error: isError,
              onChange: handleChange,
              size,
              fixedRowsCount
            }
          ),
          /* @__PURE__ */ jsxs(Stack, { sx: styles.hintRow, children: [
            hint,
            max && /* @__PURE__ */ jsxs(
              Typography,
              {
                sx: styles.max,
                color: disabled ? "grey.400" : isError ? "radicalRed.900" : "grey.800",
                children: [
                  symbolsEntered,
                  "/",
                  max
                ]
              }
            )
          ] })
        ]
      }
    );
  }
);
TextareaControl.displayName = "TextareaControl";
export {
  TextareaControl
};
//# sourceMappingURL=TextareaControl.js.map
